/**
 * Actor Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Actor TypeScript type and Firestore data.
 *
 * CRITICAL: Uses Client SDK signature - fromFirestore(snapshot, options)
 * NOT Admin SDK signature - fromFirestore(data)
 *
 * Note: Actor is a subcollection of Audience
 * Path: projects/{projectId}/audiences/{audienceId}/actors/{actorId}
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { Actor } from '@segmently/schema-models/entities';

export const actorConverter: FirestoreDataConverter<Actor> = {
  /**
   * Convert Actor model to Firestore data
   * Called when writing to Firestore
   */
  toFirestore(actor: Actor): any {
    const result: any = { ...actor };

    // Convert ISO string dates to Firestore Timestamps
    if (typeof result['creationDate'] === 'string') {
      result['creationDate'] = Timestamp.fromDate(new Date(result['creationDate']));
    } else if (result['creationDate'] instanceof Date) {
      result['creationDate'] = Timestamp.fromDate(result['creationDate']);
    }

    // Remove subcollection fields - not stored in document
    delete result['coreJobs'];
    delete result['contexts'];
    delete result['fears'];
    delete result['bigJobs'];
    delete result['subtasks'];
    delete result['competitors'];
    delete result['habits'];

    return result;
  },

  /**
   * Convert Firestore data to Actor model
   * Called when reading from Firestore
   *
   * CRITICAL: Client SDK signature - takes snapshot, not raw data
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Actor {
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Convert Firestore Timestamps to Date
    if (result['creationDate']?.toDate) {
      result['creationDate'] = result['creationDate'].toDate();
    } else if (typeof result['creationDate'] === 'string') {
      result['creationDate'] = new Date(result['creationDate']);
    }

    return result as Actor;
  }
};

export default actorConverter;
